import { format, parseISO } from "date-fns";
import React from "react";

import { PitchTypeTag } from "./PitchTypeTag";

interface MovementCardProps {
  movement: any;
  isExpanded: boolean;
  avatarOffsets: Record<string, { x: number; y: number; z: number }>;
  avatarRotations: Record<string, { x: number; y: number; z: number }>;
  openColorDropdowns: Set<string>;
  openPositionDropdowns: Set<string>;
  colorPalette: string[];
  pitches?: Record<string, any>;
  enabledMetrics?: string[];
  selectedSortColumn?: string | null;
  onMovementClick: (movementId: string) => void;
  onColorChange: (movementId: string, newColor: string) => void;
  onAvatarOffsetChange: (
    movementId: string,
    axis: string,
    value: number,
  ) => void;
  onAvatarRotationChange: (
    movementId: string,
    axis: string,
    value: number,
  ) => void;
  onResetAvatarPosition: (movementId: string) => void;
  onResetAvatarRotation: (movementId: string) => void;
  onResetAllAvatar: (movementId: string) => void;
  onToggleColorDropdown: (movementId: string, e: React.MouseEvent) => void;
  onTogglePositionDropdown: (movementId: string, e: React.MouseEvent) => void;
}

export const MovementCard: React.FC<MovementCardProps> = ({
  movement,
  isExpanded,
  avatarOffsets,
  avatarRotations,
  openColorDropdowns,
  openPositionDropdowns,
  colorPalette,
  pitches,
  enabledMetrics = [],
  selectedSortColumn = null,
  onMovementClick,
  onColorChange,
  onAvatarOffsetChange,
  onAvatarRotationChange,
  onResetAvatarPosition,
  onResetAvatarRotation,
  onResetAllAvatar,
  onToggleColorDropdown,
  onTogglePositionDropdown,
}) => {
  // Helper to get the unit suffix based on metric name
  const getMetricUnit = (metricKey: string): string => {
    const lowerKey = metricKey.toLowerCase();
    if (lowerKey.includes("angle") || lowerKey.includes("ang")) {
      return "°";
    }
    if (lowerKey.includes("speed")) {
      return " mph";
    }
    return "";
  };

  // Helper to get metric value from pitch metadata
  const getMetricValue = (metricKey: string): string => {
    const pitch = pitches?.[movement.id];
    if (!pitch) return "N/A";

    // Check metadata first (snake_case)
    const metadataValue = pitch.metadata?.[metricKey];
    if (metadataValue !== undefined && metadataValue !== null) {
      return typeof metadataValue === "number"
        ? metadataValue.toFixed(2)
        : String(metadataValue);
    }

    // Check first frame
    const frameValue = pitch.frames?.[0]?.[metricKey];
    if (frameValue !== undefined && frameValue !== null) {
      return typeof frameValue === "number"
        ? frameValue.toFixed(2)
        : String(frameValue);
    }

    return "N/A";
  };
  return (
    <div
      className={`p-3 border rounded-lg mb-2 cursor-pointer transition-all duration-200 bg-white hover:bg-gray-50 ${
        movement.isSelected ? "border-secondary-900" : "border-gray-300"
      }`}
      onClick={() => onMovementClick(movement.id)}
      data-testid={`movement-item-${movement.id}`}
      role="button"
      tabIndex={0}
      aria-label={`Movement ${movement.id}, ${movement.pitchType} pitch, ${format(parseISO(movement.sessionDate), "M/d/yyyy")}${movement.isSelected ? ", selected" : ""}`}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onMovementClick(movement.id);
        }
      }}
    >
      <div className="flex justify-between items-center mb-2">
        <p
          className={`text-base font-semibold ${movement.isSelected ? "text-secondary-900" : "text-gray-900"}`}
          data-testid="movement-date"
        >
          {format(parseISO(movement.sessionDate), "M/d/yyyy")}
        </p>
        <PitchTypeTag pitchType={movement.pitchType} />
      </div>

      <div className="text-sm leading-5 font-medium text-gray-500 flex flex-col mt-2 space-y-1">
        <p className="truncate" data-testid="movement-id">
          ID: {movement.id}
        </p>
        {selectedSortColumn === "movement_num" && (
          <p data-testid="movement-number">
            Movement #: {movement.movementNum}
          </p>
        )}
        {enabledMetrics.length > 0 &&
          enabledMetrics.map((metric) => (
            <p key={metric} data-testid={`movement-metric-${metric}`}>
              Metric: {metric.replace(/_/g, " ")} = {getMetricValue(metric)}
              {getMetricUnit(metric)}
            </p>
          ))}
      </div>

      {/* Expanded content with color and position selection */}
      {isExpanded && (
        <div className="mt-1 pt-3">
          <div className="flex items-center justify-between gap-3">
            {/* Color Dropdown */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <button
                  onClick={(e) => onToggleColorDropdown(movement.id, e)}
                  className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors"
                  data-testid="color-dropdown-button"
                >
                  <div
                    className="w-4 h-4"
                    style={{ backgroundColor: movement.color }}
                  />
                  <svg
                    className={`w-4 h-4 text-gray-500 transition-transform ${
                      openColorDropdowns.has(movement.id) ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* AvatarPosition Dropdown */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <button
                  onClick={(e) => onTogglePositionDropdown(movement.id, e)}
                  className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors"
                  data-testid="avatar-position-dropdown-button"
                >
                  <span className="text-sm text-gray-700">Position</span>
                  <svg
                    className={`w-4 h-4 text-gray-500 transition-transform ${
                      openPositionDropdowns.has(movement.id) ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Color palette at bottom of card */}
      {openColorDropdowns.has(movement.id) && (
        <div className="mt-3 pt-1" data-testid="color-palette">
          <div className="grid grid-cols-11 gap-x-4 gap-y-4">
            {colorPalette.map((color) => (
              <button
                key={color}
                onClick={(e) => {
                  e.stopPropagation();
                  onColorChange(movement.id, color);
                }}
                className={`w-5 h-5 transition-all hover:scale-110 ${
                  movement.color === color
                    ? "border border-black scale-110"
                    : ""
                }`}
                style={{ backgroundColor: color }}
                data-testid={`color-option-${color}`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Avatar position controls at bottom of card */}
      {openPositionDropdowns.has(movement.id) && (
        <div className="mt-3 pt-1" data-testid="avatar-position-controls">
          <div className="grid grid-cols-2 gap-3">
            {/* Position Controls */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <h4 className="text-xs font-medium text-gray-700">Position:</h4>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onResetAvatarPosition(movement.id);
                  }}
                  className="w-5 h-5 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Reset position to default"
                >
                  ↺
                </button>
              </div>
              {["X", "Y", "Z"].map((axis) => (
                <div key={axis} className="mb-1.5">
                  <div
                    className="flex items-center gap-2"
                    style={{
                      width: "140px",
                      height: "28px",
                      padding: "4px 8px",
                      borderRadius: "6px",
                      border: "1px solid #D1D5DB",
                      background: "#FFFFFF",
                      boxShadow: "0px 1px 2px 0px #0000000D",
                    }}
                  >
                    <label className="text-sm text-gray-600 w-4">{axis}:</label>
                    <input
                      type="range"
                      min="-2"
                      max="2"
                      step="0.1"
                      value={
                        avatarOffsets[movement.id]?.[
                          axis.toLowerCase() as "x" | "y" | "z"
                        ] || 0
                      }
                      className="appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[10px] [&::-webkit-slider-thumb]:h-[10px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#FB6A07] [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-[10px] [&::-moz-range-thumb]:h-[10px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#FB6A07] [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer"
                      style={{
                        width: "70px",
                        height: "5px",
                        background: `linear-gradient(to right, #FB6A07 0%, #FB6A07 ${(((avatarOffsets[movement.id]?.[axis.toLowerCase() as "x" | "y" | "z"] || 0) + 2) / 4) * 100}%, #D1D5DB ${(((avatarOffsets[movement.id]?.[axis.toLowerCase() as "x" | "y" | "z"] || 0) + 2) / 4) * 100}%, #D1D5DB 100%)`,
                        borderRadius: "2.5px",
                        outline: "none",
                      }}
                      onChange={(e) => {
                        const newValue = parseFloat(e.target.value);
                        onAvatarOffsetChange(
                          movement.id,
                          axis.toLowerCase(),
                          newValue,
                        );
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onMouseUp={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                      onTouchEnd={(e) => e.stopPropagation()}
                    />
                    <span className="text-sm text-gray-600 w-8 text-center">
                      {(
                        avatarOffsets[movement.id]?.[
                          axis.toLowerCase() as "x" | "y" | "z"
                        ] || 0
                      ).toFixed(1)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Rotation Controls */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <h4 className="text-xs font-medium text-gray-700">Rotation:</h4>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onResetAvatarRotation(movement.id);
                  }}
                  className="w-5 h-5 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Reset rotation to default"
                >
                  ↺
                </button>
              </div>
              {["X", "Y", "Z"].map((axis) => (
                <div key={axis} className="mb-1.5">
                  <div
                    className="flex items-center gap-2"
                    style={{
                      width: "140px",
                      height: "28px",
                      padding: "4px 8px",
                      borderRadius: "6px",
                      border: "1px solid #D1D5DB",
                      background: "#FFFFFF",
                      boxShadow: "0px 1px 2px 0px #0000000D",
                    }}
                  >
                    <label className="text-sm text-gray-600 w-4">{axis}:</label>
                    <input
                      type="range"
                      min="-45"
                      max="45"
                      step="1"
                      value={
                        avatarRotations[movement.id]?.[
                          axis.toLowerCase() as "x" | "y" | "z"
                        ] || 0
                      }
                      className="appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[10px] [&::-webkit-slider-thumb]:h-[10px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#FB6A07] [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-[10px] [&::-moz-range-thumb]:h-[10px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#FB6A07] [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer"
                      style={{
                        width: "70px",
                        height: "5px",
                        background: `linear-gradient(to right, #FB6A07 0%, #FB6A07 ${(((avatarRotations[movement.id]?.[axis.toLowerCase() as "x" | "y" | "z"] || 0) + 45) / 90) * 100}%, #D1D5DB ${(((avatarRotations[movement.id]?.[axis.toLowerCase() as "x" | "y" | "z"] || 0) + 45) / 90) * 100}%, #D1D5DB 100%)`,
                        borderRadius: "2.5px",
                        outline: "none",
                      }}
                      onChange={(e) => {
                        const newValue = parseFloat(e.target.value);
                        onAvatarRotationChange(
                          movement.id,
                          axis.toLowerCase(),
                          newValue,
                        );
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onMouseUp={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                      onTouchEnd={(e) => e.stopPropagation()}
                    />
                    <span className="text-sm text-gray-600 w-8 text-center">
                      {avatarRotations[movement.id]?.[
                        axis.toLowerCase() as "x" | "y" | "z"
                      ] || 0}
                      °
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Reset All Button */}
          <div className="mt-3 pt-2 border-t border-gray-200">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onResetAllAvatar(movement.id);
              }}
              className="w-full text-xs px-3 py-2 bg-gray-100 text-gray-600 border border-gray-300 rounded hover:bg-gray-200 transition-colors"
              title="Reset all position and rotation values to default"
            >
              Reset All
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
